import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { Messages } from '../../../codegen/Localize';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IDataSourceInfo } from '../../../ts/models/IDataSourceInfo';
import { BackgroundJobs } from '../../../ts/resources/BackgroundJobs';
import { Databases } from '../../../ts/resources/Databases';
import { DataConnections } from '../../../ts/resources/DataConnections';
import { DataRoles } from '../../../ts/resources/Dataroles';
import { DataSources } from '../../../ts/resources/Datasources';
import { Flows } from '../../../ts/resources/Flows';
import { Projects } from '../../../ts/resources/Projects';
import { IActionResource } from '../../../ts/resources/Resource';
import { Tables } from '../../../ts/resources/Tables';
import { Views } from '../../../ts/resources/Views';
import { Workbooks } from '../../../ts/resources/Workbooks';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { AvailableActionsForEntityRequest } from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import {
  availableActionForEntityRequestSucceeded,
  AvailableActionsForEntityRequestAction,
} from '../../actions/spaces/AvailableActionsForEntityActions';
import { selectSpaceInfo } from '../../selectors/ContentSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { getCommonDatasourceInfo } from '../../spaces/CommonDatasourceInfo';
import { AvailableActions } from '../../state/AvailableActionsForEntityState';
import { SpaceInfo } from '../../state/SpaceInfo';
import { getCorrectEntityIdForActions } from './SpacesSagaHelpers';


const GetActionResourceForContentType: Partial<Record<ExplorableItemType, () => IActionResource<AvailableActions>>> = {
  workbook: () => ServiceLocator.get<Workbooks>('Workbooks'),
  project: () => ServiceLocator.get<Projects>('Projects'),
  datasource: () => ServiceLocator.get<DataSources>('DataSources'),
  dataRole: () => ServiceLocator.get<DataRoles>('DataRoles'),
  view: () => ServiceLocator.get<Views>('Views'),
  dataconnection: () => ServiceLocator.get<DataConnections>('DataConnections'),
  flow: () => ServiceLocator.get<Flows>('Flows'),
  backgroundJob: () => ServiceLocator.get<BackgroundJobs>('BackgroundJobs'),
  table: () => ServiceLocator.get<Tables>('Tables'),
  database: () => ServiceLocator.get<Databases>('Databases'),
};

function getResourceContentId(
  entityType: ExplorableItemType,
  entityId: string,
  spaceInfo: SpaceInfo,
): string {
  if (entityType === 'extractTask') {
    return spaceInfo.item.id;
  }
  return entityId;
}

function getResourceContentType(
  entityType: ExplorableItemType,
  spaceInfo: SpaceInfo
): ExplorableItemType {
  if (entityType === 'extractTask') {
    return spaceInfo.item.contentType;
  }
  return entityType;
}

/**
 * We request actions only for the ExplorableItemTypes that have an API call.
 * For other types there aren't APIs that retrieve the allowed actions;
 * instead, we determine allowed actions by looking at properties of those items and the site role.
 */
export function fetchAvailableActionsForEntity(
  entityType: ExplorableItemType,
  entityId: string,
  spaceInfo: SpaceInfo
): ng.IPromise<AvailableActions> {
  const resourceContentId = getResourceContentId(entityType, entityId, spaceInfo);
  const resourceContentType = getResourceContentType(entityType, spaceInfo);
  const actionResourceGetter = GetActionResourceForContentType[resourceContentType];
  const actionResource = actionResourceGetter && actionResourceGetter();
  if (actionResource) {
    return actionResource.actions(resourceContentId);
  }
  return null;
}

export function* processAvailableActionsForEntityRequest(action: AvailableActionsForEntityRequestAction) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    const commonDatasource = getCommonDatasourceInfo(action.item as IContentInfo<'datasource'>);

    if (action.item.contentType === 'datasource' && DataSources.isEmbedded(commonDatasource)) {
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, null));
    } else {
      let entityIdForActions = yield call(getCorrectEntityIdForActions, action.item.contentType, action.item.id);

      // Hack to use `vizportalId` field for Database & Table
      // TFS912863: Remove this hack once we switched to use vizportalId as primary ID for database & table
      if (action.item.contentType === 'table' ||
          action.item.contentType === 'database') {
        entityIdForActions = action.item.vizportalId;
      }

      const availableActions = yield call(fetchAvailableActionsForEntity, action.item.contentType, entityIdForActions,
          spaceInfo);
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootAvailableActionsForEntitySaga(): SagaIterator {
  yield takeLatest(AvailableActionsForEntityRequest, processAvailableActionsForEntityRequest);
}
